import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Tab} from '../../_models/tab';
import {addCurrency, subtractCurrency} from '../../_utils/currency-math';
import {OrderService} from '../../_services/order.service';
import {Order} from '../../_models/order';
import {DisplayOrderComponent} from '../display-order/display-order.component';
import {TabStatuses} from '../../_constants/tab-statuses';
import {LoadingService} from '../../_services/loading.service';
import {OrderStatuses} from '../../_constants/order-statuses';
import {BaseConfirmationComponent} from '../base-confirmation.component';
import {MenuItemService} from '../../_services/menu-item.service';
import {ModalController} from '@ionic/angular';
import {GiftCardAction} from '../../_models/gift-card-action';
import {Tax} from '../../_models/tax';
import {TabCredit} from '../../_models/tab-credit';
import {User} from '../../_models/user';
import {FeatureKeys} from '../../_constants/feature-keys';
import {UserService} from '../../_services/user.service';
import {CompDefinitionTypes} from '../../_constants/comp-definition-types';
import {CompDefinition} from '../../_models/comp-definition';
import {OrderItem} from '../../_models/order-item';
import {OrderItemSelectionItem} from '../../_models/order-item-selection-item';
import {CustomDollarAmountComponent} from '../custom-dollar-amount/custom-dollar-amount.component';
import {GiftCard} from '../../_models/gift-card';
import {TipComponent} from '../tip/tip.component';
import {OrderItemService} from '../../_services/order-item.service';
import {DeviceService} from '../../_services/device.service';
import {clone} from 'chart.js/helpers';
import {PickupLocationMenuService} from '../../_services/pickup-location-menu.service';
import {MembershipService} from '../../_services/membership.service';
import {OrganizationTerminalService} from '../../_services/organization-terminal.service';
import {Menu} from '../../_models/menu';
import {ImageCacheService} from '../../_services/image-cache.service';
import {TabCalculationService} from '../../_services/tab-calculation.service';
import {TabService} from '../../_services/tab.service';

@Component({
    selector: 'bb-confirm-tab',
    templateUrl: './confirm-tab.component.html',
    styleUrls: ['./confirm-tab.component.scss'],
})
export class ConfirmTabComponent extends BaseConfirmationComponent implements OnInit, OnChanges {
    @Input() tip: number;
    @Input() donation: number;
    @Input() splitAmount: number;
    @Input() totalPayment: number;
    @Input() displayTipSelection = false;
    @Input() displayChargeAuth = false;
    @Input() displayChargeAuthTip = false;
    @Input() maxDonation: number;
    @Input() maxTip: number;
    @Input() paymentMethod: string;
    @Input() managerApp = false;
    @Input() manualCardEntry = false;
    @Input() applyingGiftCard: GiftCard;
    @Input() availableOrderComps: CompDefinition[];
    @Input() tab: Tab;

    @ViewChild(DisplayOrderComponent) displayOrder: DisplayOrderComponent;
    @ViewChild(TipComponent) tipComponent: TipComponent;

    @Output() refreshTab: EventEmitter<any> = new EventEmitter();
    @Output() orderEmpty: EventEmitter<any> = new EventEmitter();
    @Output() tipChange = new EventEmitter<number>();
    @Output() donationChange = new EventEmitter<number>();
    @Output() dueChange = new EventEmitter<number>();
    @Output() deleteGiftCardPayment: EventEmitter<GiftCardAction> = new EventEmitter();
    @Output() deleteCredit: EventEmitter<TabCredit> = new EventEmitter();
    @Output() addCredit = new EventEmitter<OrderItem>();
    @Output() removeCredit = new EventEmitter<any>();
    @Output() amountChanged = new EventEmitter<number>();
    @Output() surchargeChanged = new EventEmitter<number>();
    @Output() removeTokens = new EventEmitter<OrderItem>();
    @Output() addTokens = new EventEmitter<OrderItem>();
    @Output() returnToTip = new EventEmitter();

    due = 0;
    tabStatuses = TabStatuses;
    taxes: Tax[];
    editTaxExemption = false;
    user: User;
    tipPoolItems = [];
    tipPoolSelectionItems = [];
    amount: number;
    surcharge: number;
    addedTaxes: number;
    tipAgainstAmount: number;
    menu: Menu;
    donationImageSrc: any;
    organizationLogoSrc: any;

    featureKeys = FeatureKeys;
    compDefinitionTypes = CompDefinitionTypes;
    subtractCurrency = subtractCurrency;

    constructor(
        private userService: UserService,
        private imageCacheService: ImageCacheService,
        organizationTerminalService: OrganizationTerminalService,
        membershipService: MembershipService,
        pickupLocationMenuService: PickupLocationMenuService,
        deviceService: DeviceService,
        orderService: OrderService,
        tabCalculationService: TabCalculationService,
        tabService: TabService,
        menuItemService: MenuItemService,
        orderItemService: OrderItemService,
        loadingService: LoadingService,
        modalController: ModalController,
    ) {
        super(organizationTerminalService,
            membershipService,
            pickupLocationMenuService,
            deviceService,
            menuItemService,
            orderService,
            tabCalculationService,
            tabService,
            orderItemService,
            loadingService,
            modalController);
    }

    ngOnInit() {
        this.useCached = true;

        super.ngOnInit();
        this.subscribe(this.userService.current.subscribe(u => this.user = !!u ? new User(u) : null));
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!!this.tab) {
            this.adjustForPaymentType();
        }

        if (!!changes.tab) {
            if (this.tab.status === TabStatuses.PENDING) {
                const order = this.orderService.currentValue;
                if (!!order && order.status === OrderStatuses.PENDING) {
                    this.order = order;
                } else {
                    this.order = this.tab.orders.find(o => o.status === OrderStatuses.PENDING);
                }
            } else {
                this.order = new Order({status: OrderStatuses.DELIVERED});
                this.order.items = this.tab.orders
                    .filter(o => (this.tab.status === TabStatuses.PENDING || o.status !== OrderStatuses.PENDING)
                        && o.status !== OrderStatuses.CANCELED)
                    .reduce((items, o) => {
                        return items.concat(o.items);
                    }, []);
            }

            this.tipAgainstAmount = !!this.tab ? this.tab.tipAgainstAmount : null;
        }

        if (!!changes.pickupLocation
            && !!this.pickupLocation
            && !!this.pickupLocation.activeDonationProgram
            && this.pickupLocation.activeDonationProgram.imageAddress) {
            this.donationImageSrc = this.imageCacheService.getImage(this.pickupLocation.activeDonationProgram.imageAddress);
        }

        if (!!changes.organization
            && !!this.organization
            && !!this.organization.logoAddress) {
            this.organizationLogoSrc = this.imageCacheService.getImage(this.organization.logoAddress);
        }
        this.gatherTipPoolInformation();
    }

    removeItemSave(order: Order) {
        const tab = clone<Tab>(this.tab);
        const index = tab.orders.findIndex(o => o.id === order.id
            || (order.status === OrderStatuses.PENDING && o.status === OrderStatuses.PENDING && order.deviceId === o.deviceId));
        tab.orders[index] = order;
        this.refreshTab.emit(tab);
    }

    addItemSave(order: Order) {
        const tab = clone<Tab>(this.tab);
        const index = tab.orders.findIndex(o => o.id === order.id
            || (order.status === OrderStatuses.PENDING && o.status === OrderStatuses.PENDING && order.deviceId === o.deviceId));
        tab.orders[index] = order;
        this.refreshTab.emit(tab);
    }

    refresh(t: Tab) {
        this.refreshTab.emit(t);
    }

    gatherTipPoolInformation() {
        const tipPoolItems = [];
        const tipPoolSelectionItems = [];
        if (!!this.tab && !!this.tab.orders) {
            this.tab.orders.forEach(o => {
                if (!!o.items) {
                    o.items.forEach(i => {
                        if (!!i.selectedPrice && i.selectedPrice.tipPool) {
                            tipPoolItems.push(new OrderItem(i));
                        }

                        if (!!i.selections) {
                            i.selections.forEach(s => {
                                if (!!s.selectedItems) {
                                    s.selectedItems.forEach(si => {
                                        if (!!si.orderItem && !!si.orderItem.selectedPrice && si.orderItem.selectedPrice.tipPool) {
                                            const orderItemSelectionItem = new OrderItemSelectionItem(si);
                                            const found = tipPoolSelectionItems
                                                .find(t => t.orderItem.menuItem.id === orderItemSelectionItem.orderItem.menuItem.id);
                                            if (!!found) {
                                                found.orderItem.total = addCurrency(found.orderItem.total,
                                                    orderItemSelectionItem.orderItem.total);
                                            } else {
                                                tipPoolSelectionItems.push(orderItemSelectionItem);
                                            }
                                        }
                                    });
                                }
                            });
                        }
                    });
                }
            });
        }
        this.tipPoolItems = tipPoolItems;
        this.tipPoolSelectionItems = tipPoolSelectionItems;
    }

    adjustForPaymentType() {
        if (!!this.tab) {
            this.taxes = this.tab.taxes;
            this.addedTaxes = this.tab.addedTaxes;
            this.due = this.tab.due;
            this.amount = this.tab.due;
            this.surcharge = this.tab.surcharge;
            this.amountChanged.emit(this.amount);
            this.dueChange.emit(this.due);
        }

        this.calculateSurcharge();
    }

    openEditTaxExemption() {
        this.editTaxExemption = true;
    }

    closeEditTaxExemption(taxExemptionId) {
        const tab = new Tab(this.tab);
        tab.taxExemptionId = !!taxExemptionId ? taxExemptionId : null;
        this.refreshTab.emit(tab);
        this.editTaxExemption = false;
    }

    updateGridLayout() {
        if (!!this.displayOrder) {
            this.displayOrder.updateGridLayout();
        }
    }

    tipChanged(tip: number) {
        this.tip = tip;

        this.requestDonation = !!this.pickupLocation && !!this.pickupLocation.activeDonationProgram &&
            !this.donation && this.donation !== 0;

        this.tipChange.emit(tip);
    }

    total() {
        const total = addCurrency(this.tip, this.amount);
        this.calculateSurcharge();
        return addCurrency(this.surcharge, total);
    }

    calculateSurcharge() {
        this.surcharge = this.tabCalculationService.calculateSurcharge(this.tab, this.paymentMethod, !!this.splitAmount
            ? this.splitAmount
            : this.amount, this.tip);
    }

    totalWithDonation() {
        return addCurrency(this.total(), this.donation);
    }

    totalDue() {
        return addCurrency(this.totalWithDonation(), this.tab.paymentInformation.depositsRemaining);
    }

    giftCardRemaining(payment: GiftCardAction) {
        return payment.event === 'PENDING' ? payment.balance : addCurrency(payment.balance, payment.total);
    }

    async split(): Promise<number> {
        return new Promise(async (resolve) => {
            this.tip = null;
            if (!!this.tipComponent) {
                this.tipComponent.selectedTip = null;
            }
            const modal = await this.modalController.create({
                component: CustomDollarAmountComponent,
                componentProps: {
                    amount: '',
                    max: this.tab.paymentInformation.remaining,
                    label: 'Split',
                    splitMax: subtractCurrency(this.tab.invoiced, this.tab.paymentInformation.tokenPaymentsTotal)
                },
                breakpoints: [0.0, 1],
                initialBreakpoint: 1,
                cssClass: 'quick-input-modal'
            });
            await modal.present();

            modal.onDidDismiss().then(async (returnData) => {
                if (!!returnData && (!!returnData.data || returnData.data === 0)) {
                    this.amount = Number.parseFloat(returnData.data);
                    if (this.tipAgainstAmount !== 0) {
                        this.tipAgainstAmount = this.amount;
                    }
                    this.amountChanged.emit(this.amount);
                    resolve(this.amount);
                } else {
                    resolve(null);
                }
            });
        });
    }

    handleDonation(donation: number) {
        this.donationChange.emit(donation);
    }

    authorizedTo(feature: string | string[]) {
        return !!this.user && this.user.authorizedTo(feature, this.organization.id);
    }
}
